import { stripHTML } from "@decocms/apps-website/utils/html";
import ProductCard from "~/components/product/ProductCard";
import { portugueseMappings } from "~/components/search/Sort";
import Breadcrumb from "~/components/ui/Breadcrumb";
import Icon from "~/components/ui/Icon";
import Pagination, { MAX_FROM_PARAM_VTEX, VTEX_MAX_PAGE } from "~/components/ui/Pagination";
import URLtoBreadcrumb from "~/sdk/URLtoBreadcrumb";
import { isSchedulingActive, Scheduling } from "~/sdk/contentScheduling";
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics";
import { toProductItem } from "~/sdk/ga4/transform/toProductItem";
import trimProductForShelf from "~/sdk/trimProductForShelf";
import { capitalize } from "~/sdk/utils/capitalize";
import type { BreadcrumbList, FilterToggleValue, ProductListingPage, ViewItemListParams, } from "@decocms/apps-commerce/types";

import { Picture, Source } from "~/components/ui/Picture";

import { SendEventOnView } from "~/components/Analytics";
import SendCustomEventOnView from "~/components/analytics/SendCustomEventOnView";
import { DepartmentBanners } from "~/loaders/DepartmentBanners";
import { PLPcsv } from "~/loaders/PLPCsv";
import breadcrumbOrCurrent from "~/sdk/breadcrumbOrCurrent";
import streamCSV from "~/sdk/streamCSV";
import stripTrackingParams from "~/sdk/stripTrackingParams";
import { useId } from "react";
import { minimatch } from "minimatch";
import { redirect as redirect } from "~/types/deco";
import { SITE } from "~/constants"
/**
 * @titleBy label
 */
interface Page {
    /**
     * @title Texto mostrado na lista anterior
     */
    label: string;
    page: ProductListingPage | null;
    scheduling: Scheduling;
}
export interface Props {
    /** @title Integration */
    pages: Page[];
    /**
     * @title Banners
     */
    items: DepartmentBanners[];
    /**
     * @title Parâmetros da GA4
     */
    ga4?: ViewItemListParams;
    /**
     * @title CSV para configurar as páginas do departamento/busca
     */
    csv?: PLPcsv;
}
const match = (path: string, matcher: string) => {
    // Is server-side relax
    return minimatch(path.toLowerCase(), matcher.toLowerCase());
};
type LineRedirect = {
    from?: string;
    to?: string;
    type?: string;
};
type LineCSV = {
    url?: string;
    notfound?: string;
    nofollow?: string;
};
let csvRedirectCache: LineRedirect[] | null = null;
let csvCache: LineCSV[] | null = null;
export async function loader(props: Props & { __pageUrl?: string; isMobile?: boolean }) {
    const page = props.pages.find(({ scheduling }) => isSchedulingActive(scheduling))
        ?.page ?? null;
    if (!page) {
        
    }
    if ((page?.pageInfo.currentPage ?? 1) > VTEX_MAX_PAGE) {
        redirect('?page=50');
    }
    const url = new URL(props.__pageUrl ?? "/", SITE.URL);
    const parts = url.pathname.split('/').filter(Boolean);
    const maps = url.searchParams.get('map');
    if (!maps) {
        const pageTypes = page?.pageInfo.pageTypes ?? [];
        const types = {
            'Department': 'c',
            'Category': 'c',
            'SubCategory': 'c',
            'Product': 'p',
            'Collection': 'productClusterIds',
            'Cluster': 'productClusterIds',
            'Brand': 'b',
            'Search': 'ft',
            'FullText': 'ft',
            'Unknown': 'ft',
        };
        if (pageTypes.length > 1) {
            const map = pageTypes.map((type) => types[type]).filter(Boolean).join(',');
            url.searchParams.set('map', map);
            redirect(url.href, 301);
        }
    }
    /* --------- Redirect to brand page --------- */
    const brands = page?.filters
        .find((filter) => filter.key === 'Brands')
        ?.values as FilterToggleValue[];
    if (brands?.length) {
        const term = decodeURI(url.searchParams.get('q') ?? '');
        const brand = brands.find((brand) => brand.label.toLowerCase() === term.toLowerCase());
        if (brand) {
            const brandUrl = new URL(brand.url, (props.__pageUrl ?? "/"));
            brandUrl.searchParams.delete('q');
            brandUrl.pathname = brandUrl.pathname.toLowerCase();
            redirect(brandUrl.href, 301);
        }
    }
    /* --------- Redirect to brand page --------- */
    const isSearchPage = url.pathname === '/s';
    // if (ctx.csvRedirectRegex) {
    //     try {
    //         if (!csvRedirectCache) {
    //             csvRedirectCache = [];
    //             for await (const line of streamCSV(ctx.csvRedirectRegex)) {
    //                 csvRedirectCache.push(line);
    //             }
    //         }
    //         for (const line of csvRedirectCache) {
    //             const { from, to, type } = line;
    //             if (!from || !to || !type) {
    //                 throw new Error('Missing required fields');
    //             }
    //             if (new RegExp(from).test(url.href)) {
    //                 redirect(new URL(to, url).href, type?.toLowerCase() === 'permanent' ? 301 : 307);
    //             }
    //         }
    //     }
    //     catch (error) {
    //         console.error('[CSV_REDIRECT]', error);
    //     }
    // }
    const departmentItem = props.items?.find(({ matcher }) => match(url.pathname, matcher));
    let { banner } = departmentItem ?? {};
    if (!isSchedulingActive(banner?.scheduling)) {
        banner = undefined;
    }
    let nofollow = false;
    if (props.csv) {
        try {
            if (!csvCache) {
                csvCache = [];
                for await (const line of streamCSV(props.csv)) {
                    csvCache.push(line);
                }
            }
            for (const line of csvCache) {
                const { url: urlRegex, notfound, nofollow: csvNofollow } = line;
                if (!urlRegex)
                    throw new Error('url not found');
                const urlMatch = new RegExp(urlRegex).test(url.href);
                if (urlMatch) {
                    if (notfound === '1') {
                        redirect(`${url.origin}/buscavazia?t=${parts.at(-1)}`, 301);
                        break;
                    }
                    if (csvNofollow === '1')
                        nofollow = true;
                    break;
                }
            }
        }
        catch (error) {
            console.error('[CSV_REDIRECT]', error);
        }
    }
    const notfound = false;
    const haveAnyProduct = (page?.pageInfo.records ?? 0) > 0;
    if (!page || !haveAnyProduct || notfound) {
        redirect(`${url.origin}/buscavazia?t=${parts.at(-1)}`, 301);
    }
    // `pages`/`csv`/`items` are loader-only inputs — `pages[].page` is the raw
    // ProductListingPage with FULL variants (~10 MB). The component reads only
    // the returned `page`, so keep them out of the serialized output (same rule
    // as SearchResult.tsx).
    const restProps = { ...props } as Record<string, unknown>
    delete restProps.pages
    delete restProps.csv
    delete restProps.items

    return {
        ...restProps,
        // Re-stated explicitly: `restProps` is untyped (Record<string, unknown>),
        // and the component reads `ga4` off the loader's return type.
        ga4: props.ga4,
        ...departmentItem,
        // Immutable copy — the resolved page is memo-shared with PageSEO's
        // JSON-LD (bots need full variants). See ~/sdk/trimProductForShelf.
        page: page && { ...page, products: page.products.map(trimProductForShelf) },
        banner,
        isMobile: props.isMobile ?? false,
        url: (props.__pageUrl ?? "/"),
        nofollow,
        isSearchPage,
        isPerformanceTestPage: new URL(props.__pageUrl ?? "/", SITE.URL).pathname.includes('performance'),
    };
}
export default function SearchResult({ page, descriptionRight, descriptionLeft, banner, isMobile, url, isSearchPage, ga4, isPerformanceTestPage, }: Awaited<ReturnType<typeof loader>>) {
    if (!page) {
        throw new Error('Unreachable, talvez nenhum dos agendamentos das páginas deu "match"');
    }
    const productListId = useId();
    const { products, breadcrumb, pageInfo, sortOptions } = page;
    // Não repassar tracking/PII (utm_*, gclid, ?email=…) nos links de
    // ordenação derivados desta URL (DECO-5819).
    const URLi = stripTrackingParams(new URL(url ?? ''));
    // Vtex doesn't recognize pages like sex-shop/3r-import
    // https://miess.vtexcommercestable.com.br/api/catalog_system/pub/portal/pagetype/sex-shop/3r-import
    //
    // so parts of the breadcrumb may be missing.
    // In this case I use the url to create the breadcrumb
    let breadcrumbItems: BreadcrumbList['itemListElement'] = isSearchPage
        ? breadcrumb.itemListElement
        : URLtoBreadcrumb(url);
    if (!isSearchPage) {
        for (const [i, name] of breadcrumbOrCurrent(breadcrumb.itemListElement, breadcrumbItems.map((b) => b.name ?? '')).entries()) {
            breadcrumbItems[i].name = name;
        }
    }
    // But if the two breadcrumbs are the same, I use the one from vtex
    if (breadcrumb.itemListElement.length === breadcrumbItems.length) {
        breadcrumbItems = breadcrumb.itemListElement;
    }
    const params = URLi.searchParams;
    const searchTitle = params?.get('q');
    let title = searchTitle ?? URLi.pathname
        .split('/')
        .filter(Boolean)
        .at(-1)!
        .replaceAll('-', ' ')
        .split(' ')
        .map(capitalize)
        .join(' ');
    // de 100 a 150 filter
    const priceMatch = [...title.matchAll(/De (\d*\.?\d+) A (\d*\.?\d+)/gi)];
    if (priceMatch.length) {
        title = URLi.pathname
            .split('/')
            .filter(Boolean)
            .at(-2)!
            .replaceAll('-', ' ')
            .split(' ')
            .map(capitalize)
            .join(' ');
    }
    params.delete('page');
    const selectedSortOptionLabel = params.get('O')
        ? portugueseMappings[params.get('O') as keyof typeof portugueseMappings] ?? 'Ordenar por:'
        : 'Ordenar por:';
    const _sortOptions = Object.entries(portugueseMappings)
        .filter(([key]) => sortOptions.some((option) => option.label === key))
        .map(([key, label]) => {
        const { value } = sortOptions.find(({ label }) => label === key) ?? {};
        if (!value)
            throw new Error('value not found');
        params.set('O', value);
        return {
            key: value,
            href: URLi.href,
            label,
        };
    });
    const categories = breadcrumb?.itemListElement?.reverse() ?? [];
    const viewItemListEvent = toAnalytics({
        type: 'view_item_list',
        data: {
            items: products.map((product, index) => toProductItem(product, { quantity: 1, index })),
            view: {
                id: ga4?.item_list_id ?? 'search_result',
                name: ga4?.item_list_name ??
                    stripHTML(title ?? '') ??
                    'Departamento/Resultado de busca',
            },
        },
    });
    const { records = 0, recordPerPage = 1, currentPage = 0, previousPage, nextPage } = pageInfo ??
        {};
    const pages = Math.ceil(records / (recordPerPage ?? records)) || 0;
    let lastPage = 0;
    for (let i = pages; i >= currentPage; i--) {
        const reached = i * recordPerPage - recordPerPage > MAX_FROM_PARAM_VTEX;
        if (!reached) {
            lastPage = i;
            break;
        }
    }
    const previousUrl = new URL(url ?? "/", SITE.URL);
    const nextUrl = new URL(url ?? "/", SITE.URL);
    const firstUrl = new URL(url ?? "/", SITE.URL);
    const lastUrl = new URL(url ?? "/", SITE.URL);
    if (previousPage) {
        if (currentPage > 2) {
            previousUrl.searchParams.set('page', (currentPage - 1).toString());
        }
        else {
            previousUrl.searchParams.delete('page');
        }
    }
    if (nextPage && currentPage < lastPage) {
        nextUrl.searchParams.set('page', (currentPage + 1).toString());
        lastUrl.searchParams.set('page', lastPage.toString());
    }
    if (previousPage) {
        firstUrl.searchParams.delete('page');
    }
    return (<div className='flex flex-col w-full max-w-[1810px] mx-auto'>
            {banner && (<div className='flex items-center relative m-4 mb-0 lg:m-5 lg:mb-0 min-[1810px]:mx-0'>
                    <Picture preload={true} className='w-full h-full max-w-[1800px] mx-auto'>
                        <Source media='(max-width: 1024px)' fetchPriority='high' src={banner.image.mobile} width={768} height={287}/>
                        <Source media='(min-width: 1025px)' fetchPriority='high' src={banner.image.desktop} width={1920} height={250}/>
                        <img loading='eager' src={banner.image.desktop} className='object-cover w-full h-auto' alt={banner.alt}/>
                    </Picture>
                    <div className='absolute mx-[5%] sm:mx-[9%] lg:max-w-[300px] [&_h1]:font-medium text-sm text-white [&_h1]:mb-2 [&_h1]:text-[#ff8a3d] [&_h1]:text-[26px] [&_h1]:leading-none' dangerouslySetInnerHTML={{ __html: banner.content }}/>
                </div>)}
            <div className='px-4 lg:px-5'>
                {!banner && (<h1 className='py-4 text-[#ff8a3d] text-[26px] leading-none font-medium'>
                        {decodeURIComponent(title)}
                    </h1>)}

                <div className='pt-2 pb-2 lg:pb-0 lg:pt-4'>
                    <Breadcrumb itemListElement={breadcrumbItems}/>
                    <p className='lg:hidden text-[12px] py-1'>
                        Encontramos{' '}
                        <span className='text-red-wine font-bold'>
                            {pageInfo.records} produtos
                        </span>
                    </p>
                </div>

                {isMobile && (<div className='grid grid-cols-2 gap-3 h-12'>
                        <div className='relative bg-white text-black border border-[#eee] h-12'>
                            <input id='ordenar-por' type='checkbox' className='fixed hidden peer'/>
                            <label className='px-4 h-full w-full flex justify-between items-center text-sm cursor-pointer' htmlFor='ordenar-por'>
                                <span>{selectedSortOptionLabel}</span>
                                <Icon id='FilledChevronDown' size={7}/>
                            </label>
                            <div className='absolute top-full left-0 overflow-y-auto transition-all max-h-0 bg-white border-neutral-200 w-full shadow-lg text-sm peer-checked:max-h-[400px] peer-checked:border z-[1]'>
                                <ul className='flex flex-col divide-y divide-solid divide-[#eee]'>
                                    {_sortOptions.map(({ key, label, href }) => (<li className='p-2'>
                                            <a key={key} href={href}>
                                                <span className='text-sm'>{label}</span>
                                            </a>
                                        </li>))}
                                </ul>
                            </div>
                        </div>
                    </div>)}
                <div className='grid grid-cols-1 mt-4 gap-x-[30px]'>
                    <div className='space-y-8'>
                        <div className='bg-neutral-200 rounded pl-4 pr-[10px] h-[47px] flex items-center justify-center lg:justify-between'>
                            {!isMobile && (<p className='hidden md:block'>
                                    Encontramos{' '}
                                    <span className='text-red-wine font-bold'>
                                        {pageInfo.records} produtos
                                    </span>
                                </p>)}
                            <Pagination url={url} pageInfo={pageInfo}/>
                            {_sortOptions.length > 0 && !isMobile && (<div className='hidden md:block'>
                                    <div className='relative bg-white text-black border border-[#eee] w-[144px] px-2 cursor-pointer'>
                                        <input id='ordenar-por' type='checkbox' className='fixed hidden peer'/>
                                        <label className='h-[27px] w-full flex justify-between items-center text-xs' htmlFor='ordenar-por'>
                                            <span>{selectedSortOptionLabel}</span>
                                            <Icon id='FilledChevronDown' size={7}/>
                                        </label>
                                        <div className='absolute z-[2] top-full left-0 overflow-y-auto transition-all max-h-0 peer-checked:max-h-[400px] bg-white border border-neutral-200 w-full shadow-lg text-xs'>
                                            <ul className='flex flex-col py-3 tracking-tighter'>
                                                {_sortOptions.map(({ key, label, href }) => (<li>
                                                        <a className='px-2' key={key} href={href}>
                                                            <span className='text-sm'>{label}</span>
                                                        </a>
                                                    </li>))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>)}
                        </div>
                        <div>
                            <div id={productListId} className='grid h-fit grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 items-center sm:gap-[30px] sm:px-4 md:px-[10px] md:gap-5 lg:px-[15px] lg:gap-8'>
                                {products?.map((product, index) => (<ProductCard key={`product-card-${product.productID}`} product={product} preload={index === 0} isMobile={isMobile as boolean} index={index} isPerformanceTestPage={isPerformanceTestPage}/>))}
                            </div>
                            <div className='bg-neutral-200 mt-20 rounded flex items-center justify-center h-[47px]'>
                                <Pagination url={url} pageInfo={pageInfo}/>
                            </div>
                            <div className='grid grid-cols-1 lg:grid-cols-2 mt-[90px] mb-16'>
                                {descriptionLeft && (<div className={`search-result-description space-y-3${!descriptionRight ? ' col-span-full' : ''}`} dangerouslySetInnerHTML={{
                __html: descriptionLeft,
            }}/>)}
                                {descriptionRight && (<div className={`search-result-description space-y-3${!descriptionLeft ? ' col-span-full' : ''}`} dangerouslySetInnerHTML={{
                __html: descriptionRight,
            }}/>)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SendEventOnView id={productListId} event={viewItemListEvent}/>
            {!isSearchPage && (<SendCustomEventOnView event={[
                {
                    label: 'pageCategory',
                    value: 'Departament',
                },
                {
                    label: 'departmentName',
                    value: categories.at(-1)?.name ?? '',
                },
                {
                    label: 'categoryName',
                    value: categories.at(-2)?.name ?? '',
                },
            ]}/>)}
            {isSearchPage && (<SendCustomEventOnView event={[
                {
                    label: 'pageCategory',
                    value: 'InternalSiteSearch',
                },
                {
                    label: 'siteSearchTerm',
                    value: title,
                },
            ]}/>)}
        </div>);
}
